/**
 * @copyright Copyright 2019 Epic Systems Corporation
 * @file buildAsyncFinisher internal helper function
 * @author Roland Scott
 * @module react-async-hook\internal\use-async\build-async-finisher
 */

import { StateUpdaterRef, CallbackExecutorRef, IAsyncFinisher } from "../types";
import { IAsyncFunction } from "../utility-types";

/**
 * Build a function that will be used to finish the async operation when the Promise resolves.
 * If no executeCallbacks function is given, then the finisher will only update the state - it
 * will not execute the callbacks.
 *
 * @param id The id of the execution to finish
 * @param updateState React ref to the current function used to update state
 * @param executeCallbacks React ref to the current function used to execute callbacks
 */
export function buildAsyncFinisher(
	id: number,
	updateState: StateUpdaterRef,
	executeCallbacks?: CallbackExecutorRef,
): IAsyncFinisher<IAsyncFunction> {
	if (executeCallbacks) {
		return (result, error, extras) => {
			// Update the state. Will be a no-op if the tracker component has already unmounted.
			updateState.current({ type: "finish", id, result, error });
			// Execute callbacks. Will be a no-op if this execution has been "ignored"
			executeCallbacks.current(result, error, { executionId: id, ...extras });
		};
	} else {
		return (result, error) => {
			// Update the state. Will be a no-op if the tracker component has already unmounted.
			updateState.current({ type: "finish", id, result, error });
		};
	}
}
